import { useState, useEffect, useCallback, useRef } from 'react';
import type { RadioVideo } from '@/mocks/radioContent';

interface VideoPlayerProps {
  video: RadioVideo;
  relatedVideos: RadioVideo[];
  isFavorite: boolean;
  onClose: () => void;
  onToggleFavorite: (id: number) => void;
  onShare: (video: RadioVideo) => void;
  onSelectVideo: (video: RadioVideo) => void;
}

export default function VideoPlayer({
  video,
  relatedVideos,
  isFavorite,
  onClose,
  onToggleFavorite,
  onShare,
  onSelectVideo,
}: VideoPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState('0:00');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const durationSeconds = parseDuration(video.duration);

  const startPlayback = useCallback(() => {
    setIsPlaying(true);
    intervalRef.current = setInterval(() => {
      setProgress((prev) => {
        const increment = 100 / (durationSeconds || 120);
        const next = prev + increment;
        if (next >= 100) {
          setIsPlaying(false);
          if (intervalRef.current) clearInterval(intervalRef.current);
          return 100;
        }
        return next;
      });
      setCurrentTime((prev) => {
        const parts = prev.split(':').map(Number);
        let secs = parts[0] * 60 + parts[1] + 1;
        if (secs >= durationSeconds) {
          secs = durationSeconds;
        }
        return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
      });
    }, 1000);
  }, [durationSeconds]);

  const togglePlayPause = useCallback(() => {
    if (isPlaying) {
      setIsPlaying(false);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } else {
      if (progress >= 100) {
        setProgress(0);
        setCurrentTime('0:00');
      }
      startPlayback();
    }
  }, [isPlaying, progress, startPlayback]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  useEffect(() => {
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime('0:00');
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, [video.id]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === ' ') {
        e.preventDefault();
        togglePlayPause();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, togglePlayPause]);

  const formatDuration = (d: string) => d;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/98 flex flex-col"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center cursor-pointer hover:bg-white/20 transition-colors"
          >
            <i className="ri-arrow-down-line text-white text-lg"></i>
          </button>
          <div>
            <p className="text-white text-sm font-bold leading-snug line-clamp-1">{video.title}</p>
            <p className="text-white/40 text-[11px]">
              {video.date} &bull; {video.views.toLocaleString()} vistas
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onToggleFavorite(video.id)}
            className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center cursor-pointer hover:bg-white/20 transition-colors"
          >
            <i
              className={`text-lg ${isFavorite ? 'ri-heart-fill text-red-400' : 'ri-heart-line text-white'}`}
            ></i>
          </button>
          <button
            onClick={() => onShare(video)}
            className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center cursor-pointer hover:bg-white/20 transition-colors"
          >
            <i className="ri-share-forward-line text-white text-lg"></i>
          </button>
        </div>
      </div>

      {/* Video area */}
      <div className="flex-1 flex items-center justify-center px-4 min-h-0">
        <div className="relative max-w-5xl w-full aspect-video rounded-2xl overflow-hidden bg-black/50">
          <img
            src={video.thumbnailUrl}
            alt={video.title}
            className="w-full h-full object-cover object-top"
          />

          {/* Dark overlay when paused */}
          {!isPlaying && (
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
              <button
                onClick={togglePlayPause}
                className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center cursor-pointer hover:bg-white/35 transition-all hover:scale-105"
              >
                <i className="ri-play-fill text-white text-4xl ml-1.5"></i>
              </button>
            </div>
          )}

          {/* Playing overlay */}
          {isPlaying && (
            <div
              className="absolute inset-0 flex items-center justify-center cursor-pointer"
              onClick={togglePlayPause}
            >
              <div className="w-20 h-20 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                <i className="ri-pause-fill text-white text-4xl"></i>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Controls bar */}
      <div className="px-4 md:px-8 py-3 flex-shrink-0">
        {/* Progress */}
        <div className="flex items-center gap-3 mb-2">
          <span className="text-white/70 text-xs font-mono w-10">{currentTime}</span>
          <div className="flex-1 h-1.5 rounded-full bg-white/15 cursor-pointer relative group">
            <div
              className="h-full rounded-full bg-primary-500 transition-all duration-1000 ease-linear relative"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-white opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </div>
          </div>
          <span className="text-white/70 text-xs font-mono w-10 text-right">
            {formatDuration(video.duration)}
          </span>
        </div>

        {/* Buttons */}
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={togglePlayPause}
            className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center cursor-pointer hover:bg-white/20 transition-colors"
          >
            <i
              className={`text-white text-xl ${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} ${!isPlaying ? 'ml-0.5' : ''}`}
            ></i>
          </button>
          <button className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center cursor-pointer hover:bg-white/20 transition-colors">
            <i className="ri-volume-up-line text-white text-base"></i>
          </button>
        </div>
      </div>

      {/* Related videos */}
      {relatedVideos.length > 0 && (
        <div className="px-4 md:px-8 pb-6 flex-shrink-0">
          <h3 className="text-white text-sm font-bold mb-3">Videos relacionados</h3>
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {relatedVideos.map((rv) => (
              <button
                key={rv.id}
                onClick={() => onSelectVideo(rv)}
                className={`flex-shrink-0 w-48 md:w-56 rounded-xl overflow-hidden bg-white/10 cursor-pointer transition-all hover:bg-white/15 ${
                  rv.id === video.id ? 'ring-2 ring-primary-500' : ''
                }`}
              >
                <div className="aspect-video w-full relative">
                  <img
                    src={rv.thumbnailUrl}
                    alt={rv.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded-md bg-black/70 text-white text-[10px] font-mono">
                    {rv.duration}
                  </div>
                </div>
                <div className="p-2.5 text-left">
                  <p className="text-white text-xs font-semibold leading-snug line-clamp-2">
                    {rv.title}
                  </p>
                  <p className="text-white/40 text-[10px] mt-1">
                    {rv.views.toLocaleString()} vistas &bull; {rv.date}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function parseDuration(duration: string): number {
  const parts = duration.split(':').map(Number);
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }
  return 0;
}